import type { FC } from 'react'

import { Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getSortedList } from '@skytech/utils'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useAdminAccess } from '@/hooks/access'
import { useSortParams } from '@/hooks/searchParams'
import { useLocalDateTime } from '@/hooks/use-local-date-time'
import { useRenderLimit } from '@/hooks/use-render-limit'

import { EmployeeListItem } from './EmployeeListItem'

import { ExportToExcel } from '../../components/ExportToExcel'
import type { EmployeeConsentDetails } from '../../resources'

type EmployeeListProps = {
  searchFilter: string
  employeeList: EmployeeConsentDetails['eligibleCostObjects']
  consentName: string
  openModal: (id: number) => void
}

export const EmployeeList: FC<EmployeeListProps> = ({
  searchFilter,
  consentName,
  employeeList,
  openModal,
}) => {
  useAdminAccess()

  const translate = useTranslation()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof employeesParsedForSorting)[number]>()
  const { formatTime } = useLocalDateTime()

  const employeesParsedForSorting = employeeList.map(item => ({ ...item }))

  const sortedList = getSortedList({
    list: employeesParsedForSorting,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (employeeList.length === 0) {
    return <NoResults />
  }

  const dataToExport = employeeList.map(employee => ({
    id: employee.id,
    approvalDate: employee.approvedDate ? formatTime({ date: employee.approvedDate }) : '',
    revokedDate: employee.revokedDate ? formatTime({ date: employee.revokedDate }) : '',
    name: employee.name,
    employeeNumber: employee.employeeNo,
    phoneNumber: employee.code,
    department: employee.department,
    email: employee.email,
  }))

  return (
    <>
      <ExportToExcel
        data={dataToExport}
        filename={`${translate('Consent reports for')} ${consentName.split(' ').slice(0, 5).join(' ')}...`}
      />
      <Table>
        <thead>
          <tr>
            <Th id="name" onClick={handleSort}>
              <Translation id="Name" />
            </Th>

            <Th id="approvedDate" onClick={handleSort}>
              <Translation id="Approval date" />
            </Th>

            <Th id="approvedDate" onClick={handleSort}>
              <Translation id="Revoked date" />
            </Th>

            <Th id="employeeNo" onClick={handleSort}>
              <Translation id="Employee number" />
            </Th>
            <Th id="code" onClick={handleSort}>
              <Translation id="Phone number" />
            </Th>
            <Th id="email" onClick={handleSort}>
              <Translation id="Email" />
            </Th>
            <Th id="department" onClick={handleSort}>
              <Translation id="Department" />
            </Th>
          </tr>
        </thead>
        <tbody>
          {visibleList.map(item => (
            <EmployeeListItem
              key={item.id}
              searchFilter={searchFilter}
              {...item}
              openModal={openModal}
            />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}
